import React from "react";
import { FaWhatsapp, FaDownload } from "react-icons/fa";
import {
  getMorningMealTotal,
  getMorningMealDays,
  getMorningMealRate,
  getOtherFacilityTotal,
  getOtherFacilityDetails,
  getDetailRowsByType,
  getDetailLineTotal,
} from "../utils/salaryHelpers";
import { getSalarySlipHtmlContent, getSalarySlipStyles } from "./salary-slip-export";
// import { generatePDFWithKuberLogo } from "../../../utils/printUtils";

const SalaryDetailPage = ({ salaryData, onClose }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
    });
  };

  const formatPayrollPeriod = (dateString) => {
    const date = dateString ? new Date(dateString) : new Date();
    const isMonthMarker = date.getDate() <= 25;
    const start = isMonthMarker
      ? new Date(date.getFullYear(), date.getMonth() - 1, 26)
      : new Date(date.getFullYear(), date.getMonth(), 26);
    const end = isMonthMarker
      ? new Date(date.getFullYear(), date.getMonth(), 25)
      : new Date(date.getFullYear(), date.getMonth() + 1, 25);
    const formatter = new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${formatter.format(start)} - ${formatter.format(end)}`;
  };

  const formatPayrollPeriodFromSalary = (salary = {}) => {
    if (salary.payroll_start_date && salary.payroll_end_date) {
      const formatter = new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      return `${formatter.format(new Date(salary.payroll_start_date))} - ${formatter.format(new Date(salary.payroll_end_date))}`;
    }
    return formatPayrollPeriod(salary.date_salary);
  };

  const getDetailLineLabel = (detail = {}) => {
    const quantity = Number(detail.quantity || 1);
    const amount = Number(detail.amount || 0);
    if (quantity > 1) return `${detail.name} (${quantity} x ${formatCurrency(amount)})`;
    return detail.name;
  };

  const generateSalarySummary = () => {
    if (!salaryData) return "";

    const employeeName = salaryData.user?.name || "N/A";
    const salaryDate = formatPayrollPeriodFromSalary(salaryData);
    const netSalary = formatCurrency(salaryData.total_salary);

    let summary = `*SLIP GAJI KUBER*\n\n`;
    summary += `👤 *Nama:* ${employeeName}\n`;
    summary += `📅 *Periode Gaji:* ${salaryDate}\n`;
    summary += `💰 *Gaji Bersih:* ${netSalary}\n\n`;

    summary += `*RINCIAN GAJI:*\n`;
    summary += `💼 Gaji Pokok: ${formatCurrency(salaryData.basic_salary)}\n`;

    if (getOtherFacilityTotal(salaryData) > 0) {
      summary += `🏢 Fasilitas Lain: ${formatCurrency(getOtherFacilityTotal(salaryData))}\n`;
    }
    if (getMorningMealTotal(salaryData) > 0) {
      summary += `🍽️ Uang Makan Pagi: ${getMorningMealDays(salaryData)} hari x ${formatCurrency(getMorningMealRate(salaryData))} = ${formatCurrency(getMorningMealTotal(salaryData))}\n`;
    }
    if (salaryData.total_insurances > 0) {
      summary += `🛡️ Asuransi: ${formatCurrency(
        salaryData.total_insurances
      )}\n`;
    }
    if (salaryData.total_work_bonuses > 0) {
      summary += `🎯 Bonus Kerja: ${formatCurrency(
        salaryData.total_work_bonuses
      )}\n`;
    }
    if (salaryData.total_other > 0) {
      summary += `➕ Bonus Lain: ${formatCurrency(salaryData.total_other)}\n`;
    }
    if (salaryData.total_deductions > 0) {
      summary += `➖ Potongan: -${formatCurrency(
        salaryData.total_deductions
      )}\n`;
    }

    summary += `\n*Dibuat oleh Kuber Booking App*`;

    return summary;
  };

  const handleWhatsAppShare = () => {
    const summary = generateSalarySummary();
    const encodedMessage = encodeURIComponent(summary);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  const handlePDFDownload = async () => {
    if (!salaryData) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Popup diblokir browser. Izinkan popup untuk mencetak PDF.");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Slip Gaji - ${salaryData.user?.name || "Employee"}</title>
          ${getSalarySlipStyles()}
        </head>
        <body>
          ${getSalarySlipHtmlContent(salaryData, formatCurrency)}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  if (!salaryData) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <p>No salary data available</p>
        </div>
      </div>
    );
  }

  const otherFacilityDetails = getOtherFacilityDetails(salaryData);
  const insuranceDetails = getDetailRowsByType(salaryData, "insurance", "insurances");
  const workBonusDetails = getDetailRowsByType(salaryData, "work_bonus", "work_bonuses");
  const otherBonusDetails = getDetailRowsByType(salaryData, "other", "others");
  const deductionDetails = getDetailRowsByType(salaryData, "deduction", "deductions");

  return (
    <div className="container-fluid p-0">
      {/* PDF Styling Fix */}
      <style>{`
        @media print {
          .bg-light {
            background-color: #f8f9fa !important;
            -webkit-print-color-adjust: exact;
          }
          .text-muted {
            color: #6c757d !important;
          }
          .text-success {
            color: #198754 !important;
          }
          .text-danger {
            color: #dc3545 !important;
          }
        }
        #salary-detail-content {
          color: #212529;
          font-family: 'Inter', -apple-system, sans-serif;
        }
      `}</style>

      {/* Header */}
      <div className="bg-white text-dark p-1 rounded-top border">
        <div className="d-flex justify-content-between align-items-start">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleWhatsAppShare}
            title="Bagikan ke WhatsApp"
          >
            <FaWhatsapp className="me-1" />
            WhatsApp
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handlePDFDownload}
            title="Download PDF"
          >
            <FaDownload className="me-1" />
            PDF
          </button>
        </div>
      </div>

      <div className="bg-white p-4" id="salary-detail-content">
        {/* Employee Info - Clean and Compact */}
        <div className="row mb-4">
          <div className="col-md-8">
            <div>
              <h2 className="mb-1">
                SLIP GAJI KARYAWAN
              </h2>
              <p className="mb-0">
                Periode: {formatPayrollPeriodFromSalary(salaryData)}
              </p>
            </div>
            <div className="border-start border-dark border-2 ps-3 mt-2">
              <h3 className="mb-2 ">{salaryData.user?.name || "N/A"}</h3>
              <div className="row">
                <div className="col-sm-6">
                  <span className="fw-small">
                    {salaryData.user?.email || "N/A"}
                  </span>{" "}
                  <br />
                  <span className="fw-small">
                    {salaryData.user?.phone || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            {/* identitas perusahaan */}
            <div className="text-md-end mt-3 mt-md-0">
              <img
                src="/new-logo.jpeg"
                alt="Kuber Logo"
                style={{ width: "150px", height: "auto" }}
              />
              <br />
              <span className="fw-medium">
                Br. Bayad, Desa Melinggih kelod, payangan, Gianyar, Bali 80572
              </span>
              <br />
              <span className="fw-medium"> +6285183037017</span>
            </div>
          </div>
        </div>

        {/* Salary Breakdown - Clean Table */}
        <div className="table-responsive">
          <table className="table table-bordered table-sm">
            <thead>
              <tr className="border-bottom">
                <th className="fw-bold" style={{ width: "50%" }}>
                  DESKRIPSI
                </th>
                <th className="fw-bold text-end" style={{ width: "25%" }}>
                  JUMLAH
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Basic Salary Group */}
              <tr className="table-light">
                <td colSpan="2" className="fw-bold text-uppercase text-muted">
                  Gaji Pokok
                </td>
              </tr>
              <tr className="bg-light">
                <td className="fw-bold">GAJI POKOK</td>
                <td className="text-end fw-bold text-success">
                  {formatCurrency(salaryData.basic_salary)}
                </td>
              </tr>

              {/* Additions Group */}
              <tr className="table-light">
                <td colSpan="2" className="fw-bold text-uppercase text-muted border-top">
                  Tambahan
                </td>
              </tr>

              {/* Facilities Group */}
              {getOtherFacilityTotal(salaryData) > 0 && (
                <>
                  {otherFacilityDetails.map((facility, index) => (
                    <tr key={facility.id || index} className="border-0">
                      <td className="ps-4 text-muted small">{getDetailLineLabel(facility)}</td>
                      <td className="text-end text-muted small">
                        {formatCurrency(getDetailLineTotal(facility))}
                      </td>
                    </tr>
                  ))}
                  {(otherFacilityDetails.length === 0 || otherFacilityDetails.length > 1) && (
                  <tr className="bg-light border-bottom">
                    <td className="fw-bold">Fasilitas Lain</td>
                    <td className="text-end fw-bold text-success">
                      {formatCurrency(getOtherFacilityTotal(salaryData))}
                    </td>
                  </tr>
                  )}
                </>
              )}

              {getMorningMealTotal(salaryData) > 0 && (
                <tr className="bg-light border-bottom">
                  <td className="fw-bold">
                    Uang Makan Pagi
                    <div className="text-muted small fw-normal">
                      {getMorningMealDays(salaryData)} hari x {formatCurrency(getMorningMealRate(salaryData))}
                    </div>
                  </td>
                  <td className="text-end fw-bold text-success">
                    {formatCurrency(getMorningMealTotal(salaryData))}
                  </td>
                </tr>
              )}

              {/* Insurances Group */}
              {(insuranceDetails.length > 0 || salaryData.total_insurances > 0) && (
                <>
                  {insuranceDetails.map((insurance, index) => (
                    <tr key={insurance.id || index} className="border-0">
                      <td className="ps-4 text-muted small">{getDetailLineLabel(insurance)}</td>
                      <td className="text-end text-muted small">
                        {formatCurrency(getDetailLineTotal(insurance))}
                      </td>
                    </tr>
                  ))}
                  {(insuranceDetails.length === 0 || insuranceDetails.length > 1) && (
                  <tr className="bg-light border-bottom">
                    <td className="fw-bold">Total Asuransi</td>
                    <td className="text-end fw-bold text-success">
                      {formatCurrency(salaryData.total_insurances)}
                    </td>
                  </tr>
                  )}
                </>
              )}

              {/* Work Bonuses Group */}
              {(workBonusDetails.length > 0 || salaryData.total_work_bonuses > 0) && (
                  <>
                    {workBonusDetails.map((bonus, index) => (
                      <tr key={bonus.id || index} className="border-0">
                        <td className="ps-4 text-muted small">{getDetailLineLabel(bonus)}</td>
                        <td className="text-end text-muted small">
                          {formatCurrency(getDetailLineTotal(bonus))}
                        </td>
                      </tr>
                    ))}
                    {(workBonusDetails.length === 0 || workBonusDetails.length > 1) && (
                    <tr className="bg-light border-bottom">
                      <td className="fw-bold">Total Bonus Kerja</td>
                      <td className="text-end fw-bold text-success">
                        {formatCurrency(salaryData.total_work_bonuses)}
                      </td>
                    </tr>
                    )}
                  </>
              )}

              {/* Other Bonuses Group */}
              {(otherBonusDetails.length > 0 || salaryData.total_other > 0) && (
                <>
                  {otherBonusDetails.map((other, index) => (
                    <tr key={other.id || index} className="border-0">
                      <td className="ps-4 text-muted small">{getDetailLineLabel(other)}</td>
                      <td className="text-end text-muted small">
                        {formatCurrency(getDetailLineTotal(other))}
                      </td>
                    </tr>
                  ))}
                  {(otherBonusDetails.length === 0 || otherBonusDetails.length > 1) && (
                  <tr className="bg-light border-bottom">
                    <td className="fw-bold">Total Bonus Lain</td>
                    <td className="text-end fw-bold text-success">
                      {formatCurrency(salaryData.total_other)}
                    </td>
                  </tr>
                  )}
                </>
              )}

              {/* Additions Summary */}
              <tr className="border-top border-2">
                <td className="fw-bold py-2">TOTAL TAMBAHAN</td>
                <td className="text-end fw-bold py-2 text-success">
                  {formatCurrency(
                      parseFloat(salaryData.total_facilities || 0) +
                      parseFloat(salaryData.total_insurances || 0) +
                      parseFloat(salaryData.total_work_bonuses || 0) +
                      parseFloat(salaryData.total_other || 0)
                  )}
                </td>
              </tr>

              {/* Gross Salary Summary */}
              <tr className="bg-light border-top border-2">
                <td className="fw-bold py-2">TOTAL PENDAPATAN (KOTOR)</td>
                <td className="text-end fw-bold py-2 text-success fs-5">
                  {formatCurrency(
                    parseFloat(salaryData.basic_salary || 0) +
                      parseFloat(salaryData.total_facilities || 0) +
                      parseFloat(salaryData.total_insurances || 0) +
                      parseFloat(salaryData.total_work_bonuses || 0) +
                      parseFloat(salaryData.total_other || 0)
                  )}
                </td>
              </tr>

              {/* Deductions Group */}
              <tr className="table-light">
                <td colSpan="2" className="fw-bold text-uppercase text-muted">
                  Pengurangan
                </td>
              </tr>

              {(deductionDetails.length > 0 || salaryData.total_deductions > 0) && (
                <>
                  {deductionDetails.map((deduction, index) => (
                    <tr key={deduction.id || index} className="border-0">
                      <td className="ps-4 text-muted small">{getDetailLineLabel(deduction)}</td>
                      <td className="text-end text-muted small">
                        -{formatCurrency(getDetailLineTotal(deduction))}
                      </td>
                    </tr>
                  ))}
                  {(deductionDetails.length === 0 || deductionDetails.length > 1) && (
                  <tr className="bg-light border-bottom">
                    <td className="fw-bold text-danger">Total Potongan</td>
                    <td className="text-end fw-bold text-danger">
                      -{formatCurrency(salaryData.total_deductions)}
                    </td>
                  </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}

        <div className="row mt-4">
          <div className="col-md-8">
            <div className="border-start border-dark border-2 ps-3">
              <h5 className="mb-2">Note : </h5>
              <div className="row">
                <div className="col-sm-6">
                  Dokumen ini dibuat secara otomatis oleh Sistem Kuber •{" "}
                  {formatDate(salaryData.created_at)}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            {/* identitas perusahaan */}
            <div className="text-md-end mt-3 mt-md-0">
              <h5 className="mb-2">Gaji Bersih Diterima:</h5>
              <span className="fw-bold fs-2">
                {formatCurrency(salaryData.total_salary)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryDetailPage;
